import { useTranslation } from "react-i18next";

import { useViewScope } from "@/shared/hooks/use-view-scope";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { SkillsCatalog } from "./SkillsCatalog";

/**
 * A tela de Skills de organização, de workspace e do espaço pessoal
 * (AB#85634, AC13 e AC19; AB#85637).
 *
 * **O escopo sai da navegação.** `useViewScope` já responde `organization` ou
 * `product` conforme haja um workspace selecionado, e é exatamente essa a
 * pergunta que a instalação precisa responder. Quem está na visão da
 * organização instala para a organização; quem está num workspace, no
 * workspace. Nada disso vira campo de formulário.
 */
export const SkillsPage = () => {
  const { t } = useTranslation();
  const { scope, productId, isResolving } = useViewScope();
  const { isAdmin } = useIsAdmin();
  const { isSuperAdmin } = useIsSuperAdminRole();

  if (isResolving) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Escopo de plataforma não tem organização, e sem organização não há catálogo
  // a servir. A loja da plataforma é outra tela, do AC1 deste mesmo item.
  if (scope === "platform") {
    return (
      <div className="flex min-h-[400px] items-center justify-center text-muted-foreground">
        {t("skills.selectOrganization")}
      </div>
    );
  }

  // No espaço pessoal a grade é a mesma, e a descrição diz de quem ele é: sem
  // ela, a tela é visualmente idêntica à da organização e nada indica que o que
  // se instala ali não alcança mais ninguém.
  if (scope === "user") {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{t("skills.mine.description")}</p>
        {/* No próprio espaço a pessoa manda: sem exigência de administrador
            (AC13 do #85633). Pedir aprovação seria pedir autorização para
            alguém usar uma ferramenta consigo mesmo. */}
        <SkillsCatalog target={{ scope: "user" }} canManage />
      </div>
    );
  }

  return (
    <SkillsCatalog
      target={{ scope, productId }}
      // Instalar alcança outras pessoas nestes dois níveis, então continua
      // sendo decisão de quem administra.
      canManage={isAdmin || isSuperAdmin}
    />
  );
};

export default SkillsPage;
